import "./FAQ.css";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import {
  Paper,
  Stack,
  Typography,
  Box,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CardDeck from "../../components/CardDeck/CardDeck";
import belaImg from "../../shared/img/bela_1.webp";

const QA_PAIRS = [
  {
    q: "A question/answer card is confusing. How do I rank something I don't understand?",
    a: "rank5 works in the reference frame of your group. If something's unclear, just discuss it out loud. As long as you don't hint at how you'd actually rank it, talking is totally allowed. Encouraged, even.",
  },
  {
    q: "I want to play, but I don't have friends who'd be into this. Can I only play with people I know?",
    a: "It's definitely more fun with people you know, but we've added game modes that work great with new people too. Pick Versus when you start a lobby: nobody has to guess how someone else thinks, because everyone answers the same question at once and gets scored against the group's ranking. It works with co-workers, classmates or complete strangers. Once you're past the talking stage, switch to Co-op for the deeper stuff.",
  },
  {
    q: "I want to create my own card deck. Where do I do that?",
    a: "Head to your Profile page (top-right menu). From there you can create and manage your own decks with the Card Deck Creator. Your decks can be played by anyone once published.",
  },
  {
    q: "What technologies did you use?",
    a: "Node.js and TypeScript on the backend, React and TypeScript on the frontend. Real-time communication via Socket.io, MySQL database with Prisma ORM, Docker for deployment.",
  },
  {
    q: "Why isn't there a chat function?",
    a: "rank5 is a voice game. It assumes you're in a Discord call, on Teams, FaceTiming, or ideally in the same room. If there was a text chat, I'd have to moderate it, and honestly the game is 10x better when you hear people argue about their rankings out loud.",
  },
  {
    q: "Why did you build this?",
    a: "A lot of young people spend their social time online, but the things they can actually do casually are weirdly limited. Doom-scrolling, grinding toxic ranked games, lurking in niche forums that reward being permanently online. I wanted something where you just hang out with your friends, talk about life, and have fun doing it.",
  },
  {
    q: "I find a card offensive. What should I do?",
    a: null, // Special — rendered with inline CardDeck
  },
];

export default function FAQ() {
  const history = useHistory();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const textMaxW = isMobile ? "100%" : 620;

  return (
    <div className="column rank5background faqPage">
      {/* ═══ HEADER ═══════════════════════════════════ */}
      <Box
        sx={{
          textAlign: "center",
          pt: 4,
          pb: 3,
          maxWidth: textMaxW,
          mx: "auto",
          width: "100%",
        }}
      >
        <Typography
          component="h1"
          variant="h3"
          fontWeight={800}
          sx={{ mb: 1, fontSize: { xs: 26, md: 36 } }}
        >
          FAQ
        </Typography>
      </Box>

      {/* ═══ Q&A PAIRS ════════════════════════════════ */}
      <Stack
        spacing={4}
        sx={{ maxWidth: textMaxW, mx: "auto", width: "100%", mb: 5 }}
      >
        {QA_PAIRS.map((pair, i) => (
          <Box key={i}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              {pair.q}
            </Typography>
            {pair.a ? (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ lineHeight: 1.7 }}
              >
                {pair.a}
                {/* "Why did you build this" — append game links */}
                {i === 5 && (
                  <>
                    {" "}If you're looking for more games like this:{" "}
                    <a
                      href="https://makeitmeme.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: theme.palette.primary.main }}
                    >
                      Make It Meme
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://garticphone.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: theme.palette.primary.main }}
                    >
                      Gartic Phone
                    </a>{" "}
                    are also great.
                  </>
                )}
              </Typography>
            ) : (
              /* ═══ OFFENSIVE CARD — special answer with inline CardDeck ═══ */
              <Box>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ lineHeight: 1.7, mb: 2 }}
                >
                  You have full control over your lobby's card decks. If you
                  want to play it safe, stick to featured decks. Those are
                  curated. You can recognize them by the star:
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <Box sx={{ width: 180 }}>
                  <CardDeck
                    id="faq-featured-example"
                    title={"Featured Deck\nExample"}
                    featured={true}
                    questions={48}
                    answers={210}
                    categories={12}
                    language={{ id: "en", name: "English" }}
                    creatorName="rank5team"
                    hideLanguage
                  />
                  </Box>
                </Box>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ lineHeight: 1.7 }}
                >
                  That said: ranking isn't fun when all options are agreeable.
                  The game is <em>designed</em> to confront you with things
                  you'd rank low. And when your whole group ranks something
                  dead last together, that's actually a pretty positive shared
                  experience. The interesting part is when the contrarian in
                  your group breaks rank. I always appreciated when someone in
                  my friend group came out of the contrarian closet.
                  If a specific card really crosses a line, you can report
                  individual questions and answers directly in the game.
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Stack>

      {/* ═══ BELA HONORABLE MENTION ═══════════════════ */}
      <Box sx={{ maxWidth: textMaxW, mx: "auto", width: "100%", mb: 5 }}>
        <Paper
          sx={{
            overflow: "hidden",
            borderRadius: 4,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={0}
          >
            <Box
              sx={{
                width: { xs: "100%", md: 220 },
                minHeight: { xs: 250, md: 220 },
                flexShrink: 0,
                backgroundImage: `url(${belaImg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <Box sx={{ p: { xs: 2.5, md: 3 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography
                variant="h6"
                fontWeight={700}
                color="primary"
                sx={{ mb: 1 }}
              >
                {t("honorable_mention")}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ lineHeight: 1.7 }}
              >
                {t("bela_thanks")}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>

      {/* ═══ BACK BUTTON ══════════════════════════════ */}
      <Box
        sx={{
          maxWidth: textMaxW,
          mx: "auto",
          width: "100%",
          pb: 6,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          size="large"
          onClick={() => history.push("")}
          sx={{
            borderRadius: 3,
            px: 5,
            py: 1.5,
            fontWeight: 700,
            fontSize: "1.1rem",
          }}
        >
          Back
        </Button>
      </Box>
    </div>
  );
}
